/**
 * ライセンス関連ユーティリティ（フロントエンド共通）
 */

import {
  resolveProductLicenseKeys,
  getPrimaryLicenseKey,
  normalizeLicenseKey,
  isProductLicensed,
  findContractForProduct,
  findProductByLicenseKey,
  findProductByName,
  getProductLicenseKeysByProductName,
  getProductLicenseApiNameByProductName,
} from './licenseKeyUtils.js';
import {
  isExpiryUsable,
  lastUsageDayDateFromCutoff,
  getNextBillingAnchorDate,
} from './jstDateHelper.js';

const FREE_BENEFIT_TRIAL_SOURCES = new Set(['invitation_reward', 'admin_grant']);
const FREE_BENEFIT_DEFERRAL_REASONS = new Set(['invitation_reward', 'admin_grant']);

/** 試用中の特典による無料期間（招待特典・管理者付与） */
export const isTrialFreeBenefit = (contract) =>
  FREE_BENEFIT_TRIAL_SOURCES.has(contract?.trialSource);

/** 本契約中の特典による無料期間（招待特典・管理者付与） */
export const isDeferralFreeBenefit = (contract) =>
  FREE_BENEFIT_DEFERRAL_REASONS.has(contract?.billingDeferralReason);

/**
 * 製品名・ライセンス名を正規化
 * 小文字化・空白除去により一貫した比較が可能
 * @param {string|object} name - 製品名またはライセンスオブジェクト
 * @returns {string} 正規化されたライセンス名
 */
export const toLicenseName = (name) => {
  if (!name) return '';
  const str = typeof name === 'string' ? name : (name?.name || String(name));
  return str.toLowerCase().replace(/\s+/g, '');
};

/**
 * add-license / cancel-license 等 API に渡すライセンス識別子。
 * 製品データの licenseKeys[0] / licenseName のみ（表示名の正規化は使わない）。
 */
export const getProductLicenseApiName = (product) => {
  if (!product || typeof product !== 'object') return '';
  return getPrimaryLicenseKey(product);
};

/**
 * セッションの licenses（配列 or スロットオブジェクト）から保有キー集合を構築
 */
export const buildActiveLicenseKeySet = (licenses) => {
  const keys = new Set();
  if (!licenses) return keys;

  const addRaw = (raw) => {
    const n = normalizeLicenseKey(typeof raw === 'string' ? raw : raw?.name);
    if (n) keys.add(n);
  };

  if (Array.isArray(licenses)) {
    licenses.forEach(addRaw);
  } else if (typeof licenses === 'object') {
    Object.values(licenses).forEach(addRaw);
  }
  return keys;
};

/** 契約状態からモジュール起動可否を判定（サーバー isContractUsable と整合） */
export const isContractLaunchable = (contract) => {
  if (!contract) return true;

  const status = contract.status || 'active';
  const now = Date.now();

  if (status === 'active') return true;
  if (status === 'expired') return false;
  if (status === 'trial') {
    const trialEnd = normalizeTimestamp(contract.trialEndAt);
    if (!trialEnd) return true;
    return isExpiryUsable(now, trialEnd.getTime());
  }
  if (status === 'canceled') {
    const usableUntil = normalizeTimestamp(contract.usableUntil);
    return !!(usableUntil && isExpiryUsable(now, usableUntil.getTime()));
  }
  if (status === 'past_due') {
    const graceUntil = normalizeTimestamp(contract.pastDueGraceUntil);
    return !!(graceUntil && graceUntil.getTime() > now);
  }
  return false;
};

/** React イベント等を除外し、製品オブジェクトかどうか判定 */
export const isProductRecord = (value) => (
  !!value
  && typeof value === 'object'
  && typeof value.nativeEvent === 'undefined'
  && (value.id || value.name || value.licenseName || (Array.isArray(value.licenseKeys) && value.licenseKeys.length))
);

export {
  resolveProductLicenseKeys,
  getPrimaryLicenseKey,
  normalizeLicenseKey,
  isProductLicensed,
  findContractForProduct,
  findProductByLicenseKey,
  findProductByName,
  getProductLicenseKeysByProductName,
  getProductLicenseApiNameByProductName,
};

/**
 * Firestoreタイムスタンプを正規化してDateオブジェクトに変換
 * @param {Date|object|number} timestamp - タイムスタンプ
 * @returns {Date|null} Dateオブジェクトまたはnull
 */
export const normalizeTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
  if (timestamp._seconds) return new Date(timestamp._seconds * 1000);
  if (typeof timestamp === 'number') return new Date(timestamp);
  return null;
};

/**
 * 日数を計算
 * @param {Date} fromDate - 開始日
 * @param {Date} toDate - 終了日
 * @returns {number} 日数（0以上）
 */
export const calculateRemainingDays = (fromDate, toDate) => {
  const diffMs = toDate.getTime() - fromDate.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
};

/**
 * 金額を日本円形式でフォーマット
 * @param {number} amount - 金額
 * @returns {string} フォーマットされた金額
 */
export const formatYen = (amount) => `¥${amount.toLocaleString()}`;

/**
 * 契約の表示用サマリーステータス（Dashboard 等）
 * @param {Object} contract
 * @returns {{ status: string, label: string, date: Date|null, dateLabel: string|null, priority: number }}
 */
export const getContractDisplaySummary = (contract) => {
  if (!contract) return null;
  const now = new Date();
  const status = contract.status || 'active';
  const trialEnd = normalizeTimestamp(contract.trialEndAt);
  const deferralEnd = normalizeTimestamp(contract.billingDeferralUntil);
  const usableUntil = normalizeTimestamp(contract.usableUntil);

  if (status === 'past_due') {
    const graceUntil = normalizeTimestamp(contract.pastDueGraceUntil);
    const date = graceUntil ? new Date(graceUntil) : null;
    const remainingDays = graceUntil
      ? Math.max(0, Math.ceil((graceUntil - Date.now()) / (1000 * 60 * 60 * 24)))
      : 0;
    return {
      status: 'past_due',
      label: remainingDays > 0 ? `決済未完了（猶予残${remainingDays}日）` : '決済未完了',
      date,
      dateLabel: date ? '猶予期限' : '決済・請求ページでお支払い方法をご確認ください',
      priority: 1,
    };
  }

  if (status === 'expired') {
    return {
      status: 'expired',
      label: '試用期間終了',
      date: null,
      dateLabel: null,
      priority: 6,
    };
  }

  if (status === 'trial' && trialEnd) {
    const isFreeBenefit = isTrialFreeBenefit(contract);
    return {
      status: 'trial',
      label: isFreeBenefit ? '特典による無料期間' : '試用期間中',
      date: lastUsageDayDateFromCutoff(trialEnd.getTime()),
      dateLabel: isFreeBenefit ? '特典終了日' : '試用終了日',
      priority: 2,
    };
  }

  if (status === 'active' && deferralEnd && isExpiryUsable(Date.now(), deferralEnd.getTime())) {
    const isFreeBenefit = isDeferralFreeBenefit(contract);
    return {
      status: 'billingDeferral',
      label: isFreeBenefit ? '特典による無料期間' : '支払猶予中',
      date: lastUsageDayDateFromCutoff(deferralEnd.getTime()),
      dateLabel: isFreeBenefit ? '特典終了日' : '猶予終了日',
      priority: 3,
    };
  }

  if (status === 'canceled' && usableUntil && isExpiryUsable(Date.now(), usableUntil.getTime())) {
    return {
      status: 'canceledUsable',
      label: '解約済（利用可能）',
      date: lastUsageDayDateFromCutoff(usableUntil.getTime()),
      dateLabel: '利用終了日',
      priority: 4,
    };
  }

  if (status === 'active') {
    const nextBillingDate = getNextBillingAnchorDate();
    return {
      status: 'active',
      label: 'サブスクリプション有効',
      date: nextBillingDate,
      dateLabel: '次回更新日',
      priority: 5,
    };
  }

  return null;
};

/**
 * 複数契約から Dashboard 表示用の最優先ステータスを選ぶ
 */
export const pickPrimaryContractSummary = (contracts = []) => {
  const summaries = contracts
    .map((c) => getContractDisplaySummary(c))
    .filter(Boolean)
    .sort((a, b) => a.priority - b.priority);
  return summaries[0] || null;
};

